import {Component, computed, CSSProperties} from 'vue'
import CrTransDatePicker from '@/transaction/create-transaction/CrTransDatePicker.vue'
import CrTransDebitPicker from '@/transaction/create-transaction/CrTransDebitPicker.vue'
import CrTransDescription from '@/transaction/create-transaction/CrTransDescription.vue'
import CrTransCreditPicker from '@/transaction/create-transaction/CrTransCreditPicker.vue'
import CrTransAmountInput from '@/transaction/create-transaction/CrTransAmountInput.vue'
import CrTransInvoiceInput from '@/transaction/create-transaction/CrTransInvoiceInput.vue'
import CrTransDepartmentPicker from '@/transaction/create-transaction/CrTransDepartmentPicker.vue'
import CrTransProjectPicker from '@/transaction/create-transaction/CrTransProjectPicker.vue'
import CrTransAccrStartDate from '@/transaction/create-transaction/CrTransAccrStartDate.vue'
import CrTransAccrAccountPicker from '@/transaction/create-transaction/CrTransAccrAccountPicker.vue'
import CrTransAccrMonthCount from '@/transaction/create-transaction/CrTransAccrMonthCount.vue'
import {useStorage} from '@vueuse/core'
import CrTransKid from '@/transaction/create-transaction/CrTransKid.vue'
import CrTransPaymentDate from '@/transaction/create-transaction/CrTransDueDate.vue'
import CrTransBBAN from '@/transaction/create-transaction/CrTransBBAN.vue'
import _ from 'lodash'

export enum TransactionFieldType {
    DATE = 'DATE',
    DUE_DATE = 'DUE_DATE',
    PAYMENT_DATE = 'PAYMENT_DATE',
    BBAN = 'BBAN',
    KID = 'KID',
    DESCRIPTION = 'DESCRIPTION',
    DEBIT = 'DEBIT',
    CREDIT = 'CREDIT',
    AMOUNT = 'AMOUNT',
    INVOICE_NO = 'INVOICE_NO',
    DEPARTMENTS = 'DEPARTMENTS',
    PROJECTS = 'PROJECTS',
    DIMENSIONS = 'DIMENSIONS',
    ACCR_ACCT = 'ACCR_ACCT',
    ACCR_MONTHS = 'ACCR_MONTHS',
    ACCR_START_MONTH = 'ACCR_START_MONTH'
}

export type TransactionInputField = {
    key: TransactionFieldType,
    label: string,
    skipTab?: false,
    component?: Component,
    style?: CSSProperties,
}

const tlFields = [
    {key: TransactionFieldType.DATE, label: 'Dato', component: CrTransDatePicker, style: {width: '75px'}},
    {key: TransactionFieldType.PAYMENT_DATE, label: 'Betalingsdato', component: CrTransDatePicker, style: {width: '75px'}},
    {key: TransactionFieldType.DEBIT, label: 'Debet', component: CrTransDebitPicker, style: {minWidth: '150px', maxWidth: '420px'}},
    {key: TransactionFieldType.CREDIT, label: 'Kredit', component: CrTransCreditPicker, style: {minWidth: '150px', maxWidth: '420px'}},
    {key: TransactionFieldType.DUE_DATE, label: 'Forf.dato', component: CrTransPaymentDate, style: {width: '75px'}},
    {key: TransactionFieldType.BBAN, label: 'Bankkontonr', component: CrTransBBAN, style: {minWidth: '110px'}},
    {key: TransactionFieldType.KID, label: 'KID', component: CrTransKid, style: {minWidth: '75px'}},
    {key: TransactionFieldType.DESCRIPTION, label: 'Beskrivelse', component: CrTransDescription, style: {minWidth: '90px'}},
    {key: TransactionFieldType.AMOUNT, label: 'Beløp', component: CrTransAmountInput, style: {minWidth: '90px', maxWidth: '190px'}},
    {key: TransactionFieldType.INVOICE_NO, label: 'Fakturanr', component: CrTransInvoiceInput, style: {minWidth: '65px'}},
    {key: TransactionFieldType.DEPARTMENTS, label: 'Avdeling', component: CrTransDepartmentPicker, style: {minWidth: '75px', maxWidth: '300px'}},
    {key: TransactionFieldType.PROJECTS, label: 'Prosjekt', component: CrTransProjectPicker, style: {minWidth: '75px', maxWidth: '300px'}},
    {key: TransactionFieldType.ACCR_ACCT, label: 'Periodiseringskonto', component: CrTransAccrAccountPicker, style: {minWidth: '90px'}},
    {key: TransactionFieldType.ACCR_MONTHS, label: 'Antall måneder', component: CrTransAccrMonthCount, style: {minWidth: '75px'}},
    {key: TransactionFieldType.ACCR_START_MONTH, label: 'Periodiseringsstart', component: CrTransAccrStartDate, style: {minWidth: '75px'}}
]

export type FieldConfigKey = 'incomingVouchers' | 'others'
export type FieldConfigs = Record<FieldConfigKey, {
    name: string,
    primary: TransactionFieldType[],
    expanded: TransactionFieldType[],
    hidden: TransactionFieldType[]
}>


const transactionFieldConfig = useStorage<FieldConfigs>('transactionFieldConfigs', {
    incomingVouchers: {
        name: 'Inngående faktura/kreditnota',
        primary: [TransactionFieldType.DATE, TransactionFieldType.DEBIT, TransactionFieldType.CREDIT, TransactionFieldType.DESCRIPTION, TransactionFieldType.AMOUNT, TransactionFieldType.INVOICE_NO, TransactionFieldType.DUE_DATE, TransactionFieldType.KID, TransactionFieldType.BBAN],
        expanded: [TransactionFieldType.ACCR_ACCT, TransactionFieldType.ACCR_MONTHS, TransactionFieldType.ACCR_START_MONTH, TransactionFieldType.PROJECTS, TransactionFieldType.DEPARTMENTS],
        hidden: []
    },
    others: {
        name: 'Andre bilag',
        primary: [TransactionFieldType.DATE, TransactionFieldType.DEBIT, TransactionFieldType.CREDIT, TransactionFieldType.DESCRIPTION, TransactionFieldType.AMOUNT],
        expanded: [TransactionFieldType.INVOICE_NO, TransactionFieldType.ACCR_ACCT, TransactionFieldType.ACCR_MONTHS, TransactionFieldType.ACCR_START_MONTH],
        hidden: [TransactionFieldType.PROJECTS, TransactionFieldType.DEPARTMENTS, TransactionFieldType.DUE_DATE, TransactionFieldType.KID, TransactionFieldType.BBAN]
    }
})

const selectedConfigKey = useStorage<FieldConfigKey>('selectedTransactionFieldConfig', 'incomingVouchers')

const getFieldFromKey = (key: String): TransactionInputField => {
    return tlFields.find(f => f.key === key) as TransactionInputField
}

export default () => {
    const primaryFields = computed(() => {
        const config = transactionFieldConfig.value[selectedConfigKey.value]
        return config?.primary.map(getFieldFromKey) || []
    })

    const expandedFields = computed(() => {
        const config = transactionFieldConfig.value[selectedConfigKey.value]
        return config?.expanded.map(getFieldFromKey) || []
    })

    const hiddenFields = computed(() => {
        const config = transactionFieldConfig.value[selectedConfigKey.value]
        return config?.hidden.map(getFieldFromKey) || []
    })

    const fieldConfigPresets = computed(() => {
        return Object.entries(transactionFieldConfig.value).map(entry => {
            return {
                label: entry[1].name,
                value: entry[0]
            }
        })
    })

    const getNextFieldKey = (currentKey: TransactionFieldType): TransactionFieldType | null => {
        const currentIndex = _.findIndex(primaryFields.value, f => f.key === currentKey)
        return primaryFields.value[currentIndex +1]?.key || null
    }

    return {
        fieldConfigPresets,
        selectedConfigKey,
        transactionFieldConfig,
        getFieldFromKey,
        getNextFieldKey,
        primaryFields,
        expandedFields,
        hiddenFields
    }
}